<script setup>
import { computed } from 'vue';
import { getUnixTime } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import { useExactTimestamp } from 'shared/composables/useExactTimestamp';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';

const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const { locale, t } = useI18n();

const referer = computed(() => props.conversationAttributes.referer);

const exactTimestamp = useExactTimestamp({ showTimeZone: true });

// Stored as a raw string: the visitor's `Date.toString()` for widget
// conversations and a UTC time for email ones. Re-render it in the agent's
// own timezone and locale; fall back to the raw value when unparseable.
const initiatedAt = computed(() => {
  const timestamp = props.conversationAttributes.initiated_at?.timestamp;
  if (!timestamp) return '';

  const initiatedDate = new Date(timestamp);
  if (Number.isNaN(initiatedDate.getTime())) return timestamp;

  return exactTimestamp(getUnixTime(initiatedDate));
});

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageName(props.conversationAttributes.browser_language)
);

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const visitorIp = computed(
  () =>
    props.contactAttributes.updated_at_ip ||
    props.contactAttributes.created_at_ip
);

const isPrivateIp = ipAddress => {
  const normalizedIp = ipAddress.toLowerCase();
  if (
    normalizedIp === '::1' ||
    normalizedIp.startsWith('fc') ||
    normalizedIp.startsWith('fd') ||
    normalizedIp.startsWith('fe80:')
  ) {
    return true;
  }

  const octets = normalizedIp.split('.').map(Number);
  if (octets.length !== 4 || octets.some(Number.isNaN)) return false;

  return (
    octets[0] === 10 ||
    octets[0] === 127 ||
    (octets[0] === 169 && octets[1] === 254) ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) ||
    (octets[0] === 192 && octets[1] === 168)
  );
};

const localizedCountry = computed(() => {
  const { country = '', country_code: countryCode = '' } =
    props.contactAttributes;
  if (!countryCode) return country;

  try {
    return (
      new Intl.DisplayNames([locale.value.replace('_', '-')], {
        type: 'region',
      }).of(countryCode.toUpperCase()) || country
    );
  } catch {
    return country;
  }
});

const ipLocation = computed(() => {
  if (!visitorIp.value) return '';
  if (isPrivateIp(visitorIp.value)) {
    return t('CONTACT_PANEL.PRIVATE_NETWORK');
  }

  const { region = '', city = '' } = props.contactAttributes;
  return [localizedCountry.value, region, city]
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index)
    .join(' · ');
});

const ipDetails = computed(() =>
  [
    {
      value: visitorIp.value,
      title: t('CONTACT_PANEL.IP_ADDRESS'),
      key: 'ip-address',
    },
    {
      value: ipLocation.value,
      title: t('CONTACT_PANEL.LOCATION'),
      key: 'ip-location',
    },
  ].filter(detail => detail.value)
);

const staticElements = computed(() =>
  [
    {
      content: initiatedAt,
      title: 'CONTACT_PANEL.INITIATED_AT',
      key: 'static-initiated-at',
      type: 'static_attribute',
    },
    {
      content: browserLanguage,
      title: 'CONTACT_PANEL.BROWSER_LANGUAGE',
      key: 'static-browser-language',
      type: 'static_attribute',
    },
    {
      content: referer,
      title: 'CONTACT_PANEL.INITIATED_FROM',
      key: 'static-referer',
      type: 'static_attribute',
    },
    {
      content: browserName,
      title: 'CONTACT_PANEL.BROWSER',
      key: 'static-browser',
      type: 'static_attribute',
    },
    {
      content: platformName,
      title: 'CONTACT_PANEL.OS',
      key: 'static-platform',
      type: 'static_attribute',
    },
  ].filter(attribute => !!attribute.content.value)
);
</script>

<template>
  <div class="conversation--details">
    <div
      v-if="ipDetails.length"
      class="[&>*:nth-child(odd)]:!bg-n-surface-1 [&>*:nth-child(even)]:!bg-n-slate-1 dark:[&>*:nth-child(odd)]:!bg-n-surface-2 dark:[&>*:nth-child(even)]:!bg-n-surface-1"
    >
      <div
        v-for="detail in ipDetails"
        :key="detail.key"
        class="border-b border-n-weak/50 dark:border-n-weak/90"
      >
        <ContactDetailsItem :title="detail.title" :value="detail.value" />
      </div>
    </div>
    <CustomAttributes
      :static-elements="staticElements"
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
    >
      <template #staticItem="{ element }">
        <ContactDetailsItem
          :key="element.title"
          :title="$t(element.title)"
          :value="element.content.value"
        >
          <a
            v-if="element.key === 'static-referer'"
            :href="element.content.value"
            rel="noopener noreferrer nofollow"
            target="_blank"
            class="text-n-brand"
          >
            {{ element.content.value }}
          </a>
        </ContactDetailsItem>
      </template>
    </CustomAttributes>
  </div>
</template>
